import * as React from 'react';

import { Button, EntityDetails, Row } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucidePencil, LucidePlus } from 'lucide-react';

import * as facilitiesState from '../../_state/facilities.state';
import * as state from '../../_state/tagDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AllocateMovementsToTagDialog } from './allocation/AllocateMovementsToTagDialog';
import { InventoryTagDialog } from './InventoryTagDialog';
import { InventoryTagStatusBadge } from './InventoryTagStatusBadge';
import { TagDetailOverviewContent } from './TagDetailOverviewContent';
import { TagTypeBadge } from './TagTypeBadge';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type TagDetailTab = 'overview';

export const InventoryTagDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  const [currentTab, setCurrentTab] = React.useState<TagDetailTab>('overview');
  const [addMovementsDialogOpen, setAddMovementsDialogOpen] = useAtom(
    state.$addMovementsDialogOpen,
  );
  const [editDialogOpen, setEditDialogOpen] = useAtom(state.$editDialogOpen);

  const {
    data: tag,
    isFetching,
    loadingState,
    error,
    refetch,
  } = useAtomValue(state.$remoteTagDetail);

  const facilitiesById = useAtomValue(facilitiesState.$facilitiesById);
  const facility = React.useMemo(
    () => (tag?.facilityId ? facilitiesById[tag.facilityId] : null),
    [tag?.facilityId, facilitiesById],
  );

  const handleBack = React.useCallback(() => {
    setLocation('/inventory/tags');
  }, [setLocation]);

  const handleLotClick = React.useCallback(
    (lotId: string) => {
      setLocation(`/inventory/lots/${lotId}`);
    },
    [setLocation],
  );

  const handleOpenAddMovements = React.useCallback(() => {
    setAddMovementsDialogOpen(true);
  }, [setAddMovementsDialogOpen]);

  const handleCloseAddMovements = React.useCallback(() => {
    setAddMovementsDialogOpen(false);
  }, [setAddMovementsDialogOpen]);

  const handleAddMovementsSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenEdit = React.useCallback(() => {
    setEditDialogOpen(true);
  }, [setEditDialogOpen]);

  const handleCloseEdit = React.useCallback(() => {
    setEditDialogOpen(false);
  }, [setEditDialogOpen]);

  const handleEditSuccess = React.useCallback(() => {
    refetch();
    setEditDialogOpen(false);
  }, [refetch, setEditDialogOpen]);

  const isActive =
    tag?.status === 'Active' ||
    tag?.status === 'Draft' ||
    tag?.status === 'InInventory';

  return (
    <>
      <EntityDetails
        className="@InventoryTagDetailView"
        error={error}
        loading={loadingState.isInitialLoading || isFetching}
        onBackPressed={handleBack}
        primaryTitle={
          tag ? t.tagsListView.detail.title(tag.tagNumber) : 'Loading...'
        }
        badges={
          tag ? (
            <>
              <InventoryTagStatusBadge status={tag.status} />
              <TagTypeBadge tagType={tag.tagType} />
            </>
          ) : null
        }
        subtitle={
          tag ? (
            <div className="flex flex-wrap items-center gap-x-1">
              <span className={'font-medium'}>{tag.itemName}</span>
              {tag.itemSku && (
                <>
                  <span className="text-neutral/30">・</span>
                  <span>
                    <span className="text-neutral/30">SKU</span>{' '}
                    <span className="font-medium">{tag.itemSku}</span>
                  </span>
                </>
              )}
              <span className="text-neutral/30">・</span>
              <span>{facility?.name}</span>
              {tag.lotId && tag.lotNumber && (
                <>
                  <span className="text-neutral/30">・</span>
                  <button
                    type="button"
                    onClick={() => tag.lotId && handleLotClick(tag.lotId)}
                    className="text-primary hover:underline"
                  >
                    {t.tagsListView.detail.lot}: {tag.lotNumber}
                  </button>
                </>
              )}
            </div>
          ) : undefined
        }
        headerActions={
          tag && isActive ? (
            <Row gap2>
              <Button
                icon={<LucidePencil />}
                variant="outline"
                color="neutral"
                onPress={handleOpenEdit}
              >
                {t.tagsListView.detail.edit}
              </Button>
              <Button
                icon={<LucidePlus />}
                color="primary"
                onPress={handleOpenAddMovements}
              >
                {t.tagsListView.detail.allocateMovements}
              </Button>
            </Row>
          ) : null
        }
        tabs={{
          overview: {
            title: t.tagsListView.detail.allocations,
            content: TagDetailOverviewContent,
          },
        }}
        currentTab={currentTab}
        onChangeTab={(tab) => setCurrentTab(tab as TagDetailTab)}
      />

      {tag && isActive && (
        <AllocateMovementsToTagDialog
          isOpen={addMovementsDialogOpen}
          onClose={handleCloseAddMovements}
          tagId={tag.id}
          facilityId={tag.facilityId}
          itemId={tag.itemId}
          onSuccess={handleAddMovementsSuccess}
        />
      )}

      {tag && (
        <InventoryTagDialog
          isOpen={editDialogOpen}
          onClose={handleCloseEdit}
          onSuccess={handleEditSuccess}
          editingTag={{
            id: tag.id,
            tagNumber: tag.tagNumber,
            tagType: tag.tagType,
            generatedAt: tag.generatedAt,
            status: tag.status,
            grossQuantity: tag.grossQuantity,
            tare: tag.tare,
            netQuantity: tag.netQuantity,
            itemId: tag.itemId,
            itemName: tag.itemName,
            itemSku: tag.itemSku,
            facilityId: tag.facilityId,
            lotId: tag.lotId,
            lotNumber: tag.lotNumber,
            isRevised: tag.isRevised,
            quantityCaptureMethod: 'Manual',
            tareCaptureMethod: 'Manual',
            createdAt: tag.createdAt,
            updatedAt: tag.updatedAt,
          }}
        />
      )}
    </>
  );
};
